import { FC } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { formatDateTime } from '../../../../../../../_metronic/helpers/crud-helper/helpers'
import { TriagemRiscoBadge } from '../../../../../../components/TriagemRisco/TriagemRiscoBadge'
import { TriagemTag } from '../../../../../../components/TriagemRisco/TriagemTag'
import { TriagemItem } from '../../../../triagem/core/_models'
import { rotuloOrigem, rotuloVariavel, textoValorVariavel } from './triagemTextos'

type TriagemDetalheProps = {
  triagem: TriagemItem
}

/**
 * O "porquê" de uma classificação: regras que dispararam, sinais usados, sinais que faltaram,
 * variáveis do paciente, protocolo e as decisões do médico.
 *
 * Os sinais ausentes (D15.5) são exibidos junto dos usados de propósito: sem eles o médico não tem
 * como saber que a cor saiu apenas das queixas.
 */
export const TriagemDetalhe: FC<TriagemDetalheProps> = ({ triagem }) => {
  const { t } = useT()
  const variaveis = Object.entries(triagem.variaveisUsadas ?? {})

  return (
    <div className='fs-8 text-gray-900 pt-2 pb-1 ps-2 border-start border-2'>
      <dl className='mb-3'>
        <dt className='fs-8 fw-bold text-gray-700'>{t('triagem.origem')}</dt>
        <dd className='mb-2'>{rotuloOrigem(t, triagem.origem)}</dd>

        <dt className='fs-8 fw-bold text-gray-700'>{t('triagem.classificacao')}</dt>
        <dd className='mb-2 d-flex flex-wrap align-items-center gap-2'>
          <TriagemRiscoBadge cor={triagem.corMotor} titulo={t('triagem.rotulo.cor_motor')} />
          <span className='text-muted'>{t('triagem.cor_motor')}</span>
          <TriagemRiscoBadge cor={triagem.corEfetiva} titulo={t('triagem.rotulo.cor_efetiva')} />
          <span className='text-muted'>{t('triagem.cor_efetiva')}</span>
        </dd>

        <dt className='fs-8 fw-bold text-gray-700'>{t('triagem.protocolo')}</dt>
        <dd className='mb-2'>
          {triagem.protocolo?.versao
            ? t('triagem.protocolo_versao', { versao: triagem.protocolo.versao })
            : t('triagem.protocolo')}
          {triagem.protocolo?.emValidacao && (
            <TriagemTag className='ms-2' texto={t('triagem.protocolo_em_validacao')} />
          )}
        </dd>

        <dt className='fs-8 fw-bold text-gray-700'>{t('triagem.data_classificacao')}</dt>
        <dd className='mb-2'>{formatDateTime(triagem.dtCriacao)}</dd>
      </dl>

      <section className='mb-3'>
        <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.regras_disparadas')}</h6>
        {triagem.regrasDisparadas?.length ? (
          <ul className='mb-0'>
            {triagem.regrasDisparadas.map((regra) => (
              <li key={regra.codigo} className='d-flex flex-wrap align-items-center gap-2'>
                <TriagemRiscoBadge cor={regra.cor} titulo={t('triagem.rotulo.cor_regra')} />
                <span>{regra.descricao || regra.codigo}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted mb-0'>{t('triagem.sem_regras')}</p>
        )}
      </section>

      <section className='mb-3'>
        <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.sinais_usados')}</h6>
        {triagem.sinaisUsados?.length ? (
          <ul className='mb-0'>
            {triagem.sinaisUsados.map((sinal) => (
              <li key={sinal.codigo}>
                {sinal.descricao || sinal.codigo}: <strong>{sinal.valor}</strong> {sinal.unidade}
                <span className='text-muted'> — {formatDateTime(sinal.dataAfericao)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted mb-0'>{t('triagem.sem_sinais')}</p>
        )}
      </section>

      {triagem.sinaisAusentesIndisponiveis && (
        <section className='mb-3'>
          <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.sinais_ausentes')}</h6>
          <p className='text-muted mb-0'>
            {t(
              triagem.protocolo?.emValidacao
                ? 'triagem.sinais_ausentes_indisponiveis_validacao'
                : 'triagem.sinais_ausentes_indisponiveis'
            )}
          </p>
        </section>
      )}

      {!!triagem.sinaisAusentes?.length && (
        <section className='mb-3'>
          <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.sinais_ausentes')}</h6>
          <p className='text-muted mb-1'>{t('triagem.sinais_ausentes_explicacao')}</p>
          <ul className='mb-0'>
            {triagem.sinaisAusentes.map((sinal) => (
              <li key={sinal.codigo}>{sinal.descricao || sinal.codigo}</li>
            ))}
          </ul>
        </section>
      )}

      {variaveis.length > 0 && (
        <section className='mb-3'>
          <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.variaveis_usadas')}</h6>
          <ul className='mb-0'>
            {variaveis.map(([codigo, valor]) => (
              <li key={codigo}>
                {rotuloVariavel(t, codigo)}: <strong>{textoValorVariavel(valor)}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>{t('triagem.decisoes')}</h6>
        {triagem.decisoes?.length ? (
          <ul className='mb-0'>
            {triagem.decisoes.map((decisao) => (
              <li key={decisao.id}>
                <strong>
                  {decisao.acao === 'ALTERAR'
                    ? t('triagem.decisao.alterou')
                    : t('triagem.decisao.confirmou')}
                </strong>
                {decisao.acao === 'ALTERAR' && (
                  <span className='d-inline-flex align-items-center gap-1 ms-1'>
                    <TriagemRiscoBadge
                      cor={decisao.corAnterior}
                      titulo={t('triagem.rotulo.cor_anterior')}
                    />
                    <span aria-hidden='true'>→</span>
                    <span className='visually-hidden'>{t('triagem.rotulo.para')}</span>
                    <TriagemRiscoBadge
                      cor={decisao.corFinal}
                      titulo={t('triagem.rotulo.cor_final')}
                    />
                  </span>
                )}
                <div className='text-muted'>
                  {decisao.funcionario?.nome} — {formatDateTime(decisao.dtDecisao)}
                </div>
                {decisao.motivo && (
                  <div>
                    {t('triagem.decisao.motivo')}: {decisao.motivo}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted mb-0'>{t('triagem.sem_decisoes')}</p>
        )}
      </section>
    </div>
  )
}
